import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useUserStore } from './userStore';

export interface ContentPlan {
  id: string;
  title: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

interface ContentPlanStore {
  plans: ContentPlan[];
  monthlyLimit: number;
  currentMonthPlans: number;
  nicheLimit: number;
  currentMonthNiche: number;
  lastMonthReset: string;
  addPlan: (plan: Omit<ContentPlan, 'id' | 'createdAt' | 'updatedAt'>) => boolean;
  removePlan: (id: string) => void;
  getPlan: (id: string) => ContentPlan | undefined;
  canCreatePlan: () => boolean;
  canUseNicheGenerator: () => boolean;
  incrementPlanUsage: () => boolean;
  incrementNicheUsage: () => boolean;
  resetMonthlyCount: () => void;
  getNicheLimit: () => number;
}

export const useContentPlanStore = create<ContentPlanStore>()(
  persist(
    (set, get) => ({
      plans: [],
      monthlyLimit: 1, // Free plan default
      currentMonthPlans: 0,
      nicheLimit: 1, // Free plan default
      currentMonthNiche: 0,
      lastMonthReset: new Date().toISOString(),

      addPlan: (plan) => {
        const user = useUserStore.getState().currentUser;
        if (!user) return false;

        // Get plan limits based on user role
        let limit = 1; // Free plan default
        if (user.role === 'premium' || user.role === 'premium_yearly') limit = 2;
        if (user.role === 'pro' || user.role === 'pro_yearly') limit = 5;
        if (user.role === 'admin') limit = Infinity;

        // For admin users or users within their limit
        if (user.role === 'admin' || get().currentMonthPlans < limit) {
          set((state) => ({
            plans: [
              {
                ...plan,
                id: crypto.randomUUID(),
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              },
              ...state.plans,
            ],
            currentMonthPlans: state.currentMonthPlans + (user.role === 'admin' ? 0 : 1)
          }));
          return true;
        }

        return false;
      },

      removePlan: (id) => set((state) => ({
        plans: state.plans.filter((plan) => plan.id !== id),
      })),

      getPlan: (id) => get().plans.find((plan) => plan.id === id),

      canCreatePlan: () => {
        const user = useUserStore.getState().currentUser;
        if (!user) return false;

        // Admin users have unlimited access
        if (user.role === 'admin') return true;

        // Get plan limits based on user role
        let limit = 1; // Free plan default
        if (user.role === 'premium' || user.role === 'premium_yearly') limit = 2;
        if (user.role === 'pro' || user.role === 'pro_yearly') limit = 5;

        const state = get();
        const now = new Date();
        const lastReset = new Date(state.lastMonthReset);

        // Check if we need to reset the monthly count
        if (now.getMonth() !== lastReset.getMonth() || now.getFullYear() !== lastReset.getFullYear()) {
          state.resetMonthlyCount();
          return true;
        }

        return state.currentMonthPlans < limit;
      },

      canUseNicheGenerator: () => {
        const user = useUserStore.getState().currentUser;
        if (!user) return false;

        // Admin users have unlimited access
        if (user.role === 'admin') return true;

        const state = get();
        const now = new Date();
        const lastReset = new Date(state.lastMonthReset);

        // Check if we need to reset the monthly count
        if (now.getMonth() !== lastReset.getMonth() || now.getFullYear() !== lastReset.getFullYear()) {
          state.resetMonthlyCount();
          return true;
        }

        const limit = get().getNicheLimit();
        return state.currentMonthNiche < limit;
      },

      getNicheLimit: () => {
        const user = useUserStore.getState().currentUser;
        if (!user) return 1; // Free plan default
        
        switch (user.role) {
          case 'admin':
            return Infinity;
          case 'premium':
          case 'premium_yearly':
            return 5;
          case 'pro':
          case 'pro_yearly':
            return 10;
          default:
            return 1;
        }
      },

      incrementPlanUsage: () => {
        const user = useUserStore.getState().currentUser;
        if (!user) return false;

        // Admin users have unlimited access
        if (user.role === 'admin') return true;

        const { canCreatePlan } = get();
        if (!canCreatePlan()) {
          return false;
        }

        set((state) => ({
          currentMonthPlans: state.currentMonthPlans + 1
        }));
        return true;
      },

      incrementNicheUsage: () => {
        const user = useUserStore.getState().currentUser;
        if (!user) return false;

        // Admin users have unlimited access
        if (user.role === 'admin') return true;

        const { canUseNicheGenerator } = get();
        if (!canUseNicheGenerator()) {
          return false;
        }

        set((state) => ({
          currentMonthNiche: state.currentMonthNiche + 1
        }));
        return true;
      },

      resetMonthlyCount: () => set({
        currentMonthPlans: 0,
        currentMonthNiche: 0,
        lastMonthReset: new Date().toISOString(),
      }),
    }),
    {
      name: 'content-plans',
      version: 3,
      migrate: (persistedState: any, version: number) => {
        if (version < 3) {
          return {
            ...persistedState,
            nicheLimit: 1,
            currentMonthNiche: 0,
            lastMonthReset: new Date().toISOString()
          };
        }
        return persistedState;
      }
    }
  )
);